import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { useShopContext } from "../../../context/ShopContext";
import { useCart } from "../../../context/CartContext";
import { useProductContext } from "../../../context/ProductContext";
import api from "../../../api/client";
import { Address, Order, OrderItem } from "../../../types/cart";
import { useTranslation } from "react-i18next";
import PaymentModal from "./PaymentModal";
import StatusModal from "./StatusModal";
import OrderAddressCard from "./OrderAddressCard";
import OrderItemsList from "./OrderItemsList";
import CustomerContactCard from "./CustomerContactCard";
import OrderStatusTimeline from "./OrderStatusTimeline";
import OrderDetailsLeftColumn from "./OrderDetailsLeftColumn";
import OrderDetailsRightColumn from "./OrderDetailsRightColumn";
import OrderDetailsHeader from "./OrderDetailsHeader";

// Utility to format currency
const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value);

// Status badge colors
const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  processing: "bg-blue-100 text-blue-800",
  shipped: "bg-indigo-100 text-indigo-800",
  delivered: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

const paymentStatusColors: Record<string, string> = {
  paid: "bg-green-100 text-green-800",
  unpaid: "bg-red-100 text-red-800",
};

const paymentMethodIcons: Record<string, string> = {
  visa: "💳",
  instapay: "🏦",
  wallet: "👛",
  cash: "💵",
  cash_on_delivery: "🚚",
};

const OrderDetailsPageAdmin: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { updateOrderStatus, loading, error } = useCart();
  const { products } = useProductContext();
  const { t, i18n } = useTranslation();

  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isItemsExpanded, setIsItemsExpanded] = useState(true);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isUpdatingPayment, setIsUpdatingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [showContactOptions, setShowContactOptions] = useState(false);

  const isRTL = i18n.language === "ar";

  // Fetch order details
  useEffect(() => {
    const fetchOrder = async () => {
      if (!id) {
        setFetchError(t("order.invalidOrderId"));
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await api.get<Order>(`/orders/${id}/`);
        setOrder(response.data);
      } catch (err: any) {
        setFetchError(err.response?.data?.detail || t("order.fetchError"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrder();
  }, [id, t]);

  // Handle order status update
  const handleStatusUpdate = async () => {
    if (!id || !user || !["branch_owner", "super_admin"].includes(user.role)) {
      setFetchError(t("order.unauthorizedStatusUpdate"));
      return;
    }

    try {
      await updateOrderStatus(parseInt(id), selectedStatus);
      setOrder((prev) =>
        prev ? { ...prev, status: selectedStatus as Order["status"] } : prev
      );
      setIsStatusModalOpen(false);
      setSelectedStatus("");
    } catch (err) {
      setFetchError(t("order.statusUpdateError"));
    }
  };

  // Handle payment status update
  const handlePaymentUpdate = async () => {
    if (!id || !user || !["branch_owner", "super_admin"].includes(user.role)) {
      setPaymentError(t("order.unauthorizedPaymentUpdate"));
      return;
    }

    try {
      setIsUpdatingPayment(true);
      setPaymentError(null);

      await api.patch(`/orders/${id}/`, {
        payment_status: "paid",
      });

      setOrder((prev: Order | null) =>
        prev
          ? {
              ...prev,
              payment_status: "paid",
            }
          : prev
      );

      setIsPaymentModalOpen(false);

      // Show success notification
      setTimeout(() => {
        // console.log(t("order.paymentSuccess"));
      }, 100);
    } catch (err: any) {
      setPaymentError(
        err.response?.data?.detail || t("order.paymentUpdateError")
      );
    } finally {
      setIsUpdatingPayment(false);
    }
  };

  // Get status display text
  const getStatusText = (status: string) => {
    return t(`orderStatus.${status}`);
  };

  // Get payment method display text
  const getPaymentMethodText = (method: string) => {
    return t(`paymentMethods.${method}`);
  };

  // Format phone number for WhatsApp
  const formatPhoneForWhatsApp = (phone: string) => {
    if (!phone) return "";
    // Remove any non-digit characters
    const cleaned = phone.replace(/\D/g, "");
    // If starts with 0, remove it
    if (cleaned.startsWith("0")) {
      return cleaned.substring(1);
    }
    return cleaned;
  };

  // Generate WhatsApp message
  const getWhatsAppMessage = () => {
    if (!order) return "";
    const orderNumber = order.id;
    const customerName = order.customer.username;
    const orderTotal = formatCurrency(order.total);

    const message = `مرحباً ${customerName}، 
هذا رسالة متابعة لطلبك رقم #${orderNumber} بقيمة ${orderTotal}.
حالة الطلب: ${getStatusText(order.status)}`;

    return encodeURIComponent(message);
  };



  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">{t("order.loading")}</p>
        </div>
      </div>
    );
  }

  if (fetchError || !order) {
    return (
      <div className="container mx-auto p-4 min-h-screen flex flex-col items-center justify-center">
        <div className="max-w-md w-full text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <div className="bg-red-50 text-red-600 p-6 rounded-2xl mb-6">
            <p className="text-lg font-medium">
              {fetchError || t("order.orderNotFound")}
            </p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors duration-200 font-medium"
          >
            ← {t("common.goBack")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8 ${
        isRTL ? "rtl" : "ltr"
      }`}
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <OrderDetailsHeader
        order={order}
        isRTL={isRTL}
        t={t}
        statusColors={statusColors}
        getStatusText={getStatusText}
        user={user}
        error={error || undefined}
      />

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Order Details */}
          <OrderDetailsLeftColumn
            order={order}
            user={user}
            products={products}
            isRTL={isRTL}
            t={t}
            formatCurrency={formatCurrency}
            getPaymentMethodText={getPaymentMethodText}
            paymentMethodIcons={paymentMethodIcons}
            paymentStatusColors={paymentStatusColors}
            showContactOptions={showContactOptions}
            onToggleContact={() => setShowContactOptions((prev) => !prev)}
            formatPhoneForWhatsApp={formatPhoneForWhatsApp}
            getWhatsAppMessage={getWhatsAppMessage}
            getStatusText={getStatusText}
            isItemsExpanded={isItemsExpanded}
            onToggleItems={() => setIsItemsExpanded((prev) => !prev)}
            onOpenStatusModal={() => setIsStatusModalOpen(true)}
            onOpenPaymentModal={() => setIsPaymentModalOpen(true)}
          />

          {/* Right Column - Customer & Address Info */}
          <OrderDetailsRightColumn
            order={order}
            isRTL={isRTL}
            t={t}
            formatPhoneForWhatsApp={formatPhoneForWhatsApp}
          />
        </div>
      </div>

      {/* Modals */}
      <StatusModal
        isOpen={isStatusModalOpen}
        onClose={() => {
          setIsStatusModalOpen(false);
          setSelectedStatus("");
        }}
        onConfirm={handleStatusUpdate}
        loading={loading}
        currentStatus={order?.status}
        selectedStatus={selectedStatus}
        onSelectStatus={setSelectedStatus}
        statusColors={statusColors}
        isRTL={isRTL}
        t={t}
        getStatusText={getStatusText}
      />

      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onConfirm={handlePaymentUpdate}
        isUpdating={isUpdatingPayment}
        error={paymentError}
        orderTotal={order?.total || 0}
        paymentMethod={order?.payment_method}
        isRTL={isRTL}
        t={t}
        formatCurrency={formatCurrency}
        paymentMethodIcons={paymentMethodIcons}
        getPaymentMethodText={getPaymentMethodText}
      />
    </div>
  );
};

export default OrderDetailsPageAdmin;
